import { OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { configurePageWithDefaultDebugOptions } from "@cervval.js/client-html5-core/src/components/webComponent.debug";
import { LifeCycleEvent } from "@cervval.js/client-html5-navigation/src/navigation.lifeCycle";
import { Page } from "@cervval.js/client-html5-navigation/src/navigation.page";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";

export const PAGE_SHOW_REFRESH_REASON = "pageShow";

/**
 * Abstract class used to merge the aspects of a webComponent and Page interface
 */
export abstract class WebPageComponent<I18n = unknown> extends WebComponent<I18n> implements Page {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /**
     * @param customElementOptions this allows you to override the configuration set on the @CustomElement decorator for one specific instance
     */
    constructor(customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);

        // set doNotRefreshAfterInit to true to prevent refresh("init") before refresh(PAGE_SHOW_REFRESH_REASON)
        if (this.customElementOptions?.doNotRefreshAfterInit === undefined) {
            if (this.customElementOptions == null) {
                this.customElementOptions = { doNotRefreshAfterInit: true };
            } else {
                this.customElementOptions.doNotRefreshAfterInit = true;
            }
        }

        configurePageWithDefaultDebugOptions(this);

        this.afterPageShow.on(() => {
            if (this.isInitDone) {
                // if page was already init, we simply refresh it every time we navigate to the page
                this.refresh(PAGE_SHOW_REFRESH_REASON);
            } else if (this.customElementOptions) {// this.customElementOptions is always defined if doNotRefreshAfterInit was undefined in constructor...
                // call refresh once init is done
                this.customElementOptions.doNotRefreshAfterInit = undefined;
            }
            // if page is not init yet, refresh will be called by @CustomElement decorator after init
        });
    }

    /** Retrieve the html element */
    public getHtmlElement(): Promise<HTMLElement> {
        return Promise.resolve(this);
    }

    /** Event triggered before the page is displayed when navigating to the page */
    @Event(NoBubblesEventInit)// Don't want lifecycleEvent to bubble
    public beforePageShow!: EventHandler<LifeCycleEvent>;

    /** 
     * Event triggered after the page display  when navigating to the page
     * * WARNING : this does not mean the page is init and refreshed
     */
    @Event(NoBubblesEventInit)// Don't want lifecycleEvent to bubble
    public afterPageShow!: EventHandler<LifeCycleEvent>;

    /** Event triggered before the page will be hidden / destroyed when navigating to another page */
    @Event(NoBubblesEventInit)// Don't want lifecycleEvent to bubble
    public beforePageHide!: EventHandler<LifeCycleEvent>;

    /** Event triggered after the page was hidden / destroyed when navigating to another page */
    @Event(NoBubblesEventInit)// Don't want lifecycleEvent to bubble
    public afterPageHide!: EventHandler<LifeCycleEvent>;

}
